package com.eladfinish.bible.contest.utils

import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * A coroutine-safe utility that ensures only one instance of a suspendable task runs at a time.
 * Subsequent calls can either await the ongoing task or trigger a new one based on the `force` flag.
 *
 * @param T The type of result produced by the async task.
 *
 * Usage:
 * ```
 * val lock = DeferredLock<String>()
 *
 * // Normal usage: coalesce with ongoing fetch
 * val result = lock.asyncWithLock {
 *     fetchData()
 * }
 *
 * // Forced usage: wait for current task, then start a new one
 * val freshResult = lock.asyncWithLock(force = true) {
 *     fetchFreshData()
 * }
 * ```
 *
 * Behavior:
 * - If no task is running, the block is executed and the result returned.
 * - If a task is already running:
 *   - If `force = false` (default), the result of the ongoing task is returned.
 *   - If `force = true`, this call waits for the ongoing task to finish, then runs the block and returns its result.
 */
class DeferredLock<T> {
    private val lock = Mutex()
    private var _deferred: Deferred<T>? = null

    val deferred: Deferred<T>?
        get() = _deferred

    /**
     * @param force Whether to bypass the existing deferred and start a new one after waiting for any ongoing task.
     * @param block The suspendable task to be executed if appropriate.
     * @return The result of the task.
     */
    suspend fun asyncWithLock(
        force: Boolean = false,
        block: suspend () -> T,
    ): T = coroutineScope {
        lock.withLock {
            _deferred?.takeIf { it.isActive }?.let {
                if (!force) {
                    // Returning ongoing result
                    return@withLock it
                } else {
                    // Otherwise waiting for it to finish and starting a new one
                    it.await()
                }
            }

            val task = async {
                block()
            }

            _deferred = task
            task
        }.let { currentDeferred ->
            val result = currentDeferred.await()
            // Safe lock to clear reference if not used anymore
            if (lock.tryLock()) {
                if (currentDeferred === _deferred) {
                    _deferred = null
                }
                lock.unlock()
            }
            result
        }
    }
}
